import React, { useEffect, useMemo, useState } from "react";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { initialize } from "redux-form";

// Material-UI Components
import Box from "@mui/material/Box";
import {Grid, Snackbar, Alert, Fade, Grow} from "@mui/material";


// Local Components
import GPAP_Drawer from "../../gpap-drawer/GPAP_Drawer";
import ManualFiltering from "../../../../instand/genetic_findings/manual-filtering/ManualFiltering";
import NoView from "./NoView";
import QueryGenerator from "../components/query-generator/QueryGenerator";
import Results from "../components/results/Results";

// Actions
import * as Actions from "../../../../../actions";
import * as GeneActions from "../components/side-panels/filter-panel/components/filter-sections/actions";
import * as PositionActions from "../components/side-panels/filter-panel/components/filter-sections/position-section/position_actions";
import * as QueryActions from "../../../../../actions";
import * as SelectVariantActions from "../components/results/components/variant-table/actions";
import * as SidebarActions from "../../../../../components/SideNavigation/sidebar_actions";
import * as StudyActions from "../../../reducers/study_actions";

// Services / API Calls

import { delete_query_db } from "../../../../../services/api/manage_study";

import {
    experiments_view_with_postembargo,
    getParticipantBySample,
    getSAVariants,
    getVariants
} from "../../../../../services/api/get";
import { get_all_variants, tagged_variants_request } from "../../../../../services/api/variants_api";
import MappedText from "../../../../study-page/create-study/analysis-page/components/clinical-info/mapText";

// Utils
import { assignSettings } from "../../../../study-page/create-study/search-across-all/utils";
import { check_variant_response, empty_json } from "../check_variant_response";
import { createDefaultSample, DEFAULT_GPAP_RESPONSE } from "../config_genomics";
import { date_generator } from "../../../../func/helpers";
import { default_filters } from "../../../../../config-files/configFilters";
import { getCurrentQuery } from "../components/results/components/query-applied-filters/query_difference";
import { getExperimentsViewBody, getParticipantBySampleBody } from "./Post_Objects";
import { getSamplesFromBucket } from "./helpers";
import { processParticipants } from "./helpers";

import { somatic_query_default } from "../components/results/components/variant-table/gpap-table/somatic_table/somatic_query_default";
import { query_sample } from "../../../../../services/api/genomics_query_samples/query_sample";
import { STATIC_VARIABLES } from "../../../../../config-files/const_variables";

// Additional Functions and Configurations
import { getCurrentAnalysis, getStudy } from "../components/results/components/variant-table/getAnnotations";
import { getTotalFromJson } from "../components/results/data-utils/getTotalFromJson";

// GPAP Library Components

import formatSamples from "../../../../study-page/create-study/analysis-page/formatSamples";
import map_sa_output from "../map_sa_output";
import { extractGeneNames } from "./helpers";

import configureSomaticQuery from "./configureSomaticQuerySamples";
import { DIR_GERMLINE_GT_FORM_RESET } from "../components/side-panels/filter-panel/components/filter-sections/dir-germline-gt/DirGermlineGtSection";

// Max number of SNV variants loaded into the browser for the "All" genes / initial view.
// Beyond this the payload becomes too large (~6MB per 1000 variants), so we cap and warn the user.
const max_snv_results = (window.config.max_snv_results) ? window.config.max_snv_results : 1000

const SNV_DISPLAY_LIMIT = max_snv_results;

function omitDirGermlineGtForApi(payload) {
    if (!payload || typeof payload !== "object") return payload;
    const next = { ...payload };
    delete next.dir_germline_gt;
    delete next.dir_germline_dp;
    delete next.dir_germline_gq;
    delete next.dir_germline_ad_low;
    delete next.dir_germline_ad_high;
    return next;
}

/** Collect Experiment_ID values from a CNV aggregation tree node (same shape as GeneAggTable). */
function collectCnvAggregationExperimentIds(node, out) {
    if (!node) return;
    if (Object.prototype.hasOwnProperty.call(node, "Experiment_ID") && node.Experiment_ID != null) {
        out.add(String(node.Experiment_ID));
        return;
    }
    if (Array.isArray(node.groups)) {
        node.groups.forEach((child) => collectCnvAggregationExperimentIds(child, out));
    }
}

/** Experiment IDs for a gene from raw CNV aggregation data (CNV-only genes are absent from SNV summary). */
function getExperimentIdsForCnvGene(cnvAggregations, gene) {
    if (!Array.isArray(cnvAggregations) || !gene) return [];
    const agg = cnvAggregations.find((item) => String(item.Gene_name) === String(gene));
    if (!agg) return [];
    const ids = new Set();
    collectCnvAggregationExperimentIds(agg, ids);
    return [...ids];
}

/** Union SNV-summary and CNV-aggregation experiment IDs for a gene (matches GeneAggTable merged rows). */
function mergeGeneExperimentIds(snvExperimentIds, cnvAggregations, gene, fallbackExperimentIds) {
    const snvIds = Array.isArray(snvExperimentIds) ? snvExperimentIds : [];
    const cnvIds = getExperimentIdsForCnvGene(cnvAggregations, gene);
    const merged = [...new Set([...snvIds, ...cnvIds])];
    if (merged.length > 0) return merged;
    const fallback = Array.isArray(fallbackExperimentIds) ? fallbackExperimentIds.filter(Boolean) : [];
    return [...new Set(fallback)];
}

function Genomics(props){

    // works well

    const [loading, setLoading] = useState(false);
    const [loadingParticipants, setLoadingParticipants] = useState(false);
    const [variants, setVariants] = useState({...DEFAULT_GPAP_RESPONSE});
    const [geneNames, setGeneNames] = useState([]);
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackText, setSnackText] = useState( '' );
    const [snackColor, setSnackColor] = useState( 'default' );
    const [flagged, setFlagged] = useState( false );
    // Full phenostore tag catalog for the tagged-variant filter. Kept separate from
    // `flagged`, which is limited to genes in the last SNV response for row highlighting.
    const [taggedVariantsForQuery, setTaggedVariantsForQuery] = useState(undefined);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [summary, setSummary] = useState(false);
    const [experimentGeneMap, setSampleAgg] = useState(false);
    const [studyLoaded, setStudyLoaded] = useState(false);
    const [sampleList, setSampleList] = useState([]);
    /** Full query-scope experiment IDs; not narrowed when a specific gene is selected (used for CNV aggregation). */
    const [fullExperimentIds, setFullExperimentIds] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [filters, setFilterList] = useState(default_filters);
    const [initialize, setInitialize] = useState(false);
    const [cohort, setCohort] = useState(false);
    const [selectedGeneForVariantView, setSelectedGeneForVariantView] = useState("All");
    /** Raw CNV aggregation array (filtered by gene list) for ParticipantTable counts when only CNV results exist */
    const [cnvAggregations, setCnvAggregations] = useState(null);
    /** When true: clear variant tables and show 0 without changing gene selection (so Participant table counts stay stable) */
    const [variantTablesClearedByParticipantSelection, setVariantTablesClearedByParticipantSelection] = useState(false);

    const {allDataApis } = useSelector((state) => state.generaltable);
    const selectedDir = allDataApis?.dir?.find(d => d.dir_id === allDataApis?.dirs?.dir_id);

    const cohortExperimentIds = useMemo(() => {
        if (props.studySettings?.analysisType !== "cohort" || !Array.isArray(cohort) || cohort.length === 0) return undefined;
        return cohort.map(s => s.sample_id);
    }, [props.studySettings?.analysisType, cohort]);

    const rememberFullExperimentIds = (ids) => {
        const normalized = Array.isArray(ids) ? [...new Set(ids.filter(Boolean))] : [];
        if (normalized.length > 0) {
            setFullExperimentIds(normalized);
        }
    };

    const validTypes = ["family_trio", "somatic", "instand"];

    /* This function runs the query. If a cohort is provided, runs a query against a cohort. It also add the analysis info to the store,
    the first analysis of an array */

    const runQuery = (samples_cohort) => {
        // prepare query from STORE;

        let { actions, studies, studyActions, queryActions } = props;
        // run query;

        let current_study = studies[studies.length-1];
        let first_analysis= current_study.analyses[0];

        if(first_analysis!== undefined){
            let analysis_type = first_analysis.analysis_type;

            // get the first query to run
            const FIRST_QUERY = first_analysis.queries[0];

            if(FIRST_QUERY!== undefined  ){

                // setup info for family trio analysis

                if (validTypes.includes(analysis_type)) {
                    studyActions.setStudySettings({
                        current_analysis: first_analysis.localId,
                        analysisType: analysis_type,
                        current_query: FIRST_QUERY.localId
                    });
                }

                let first_query_to_run = (FIRST_QUERY.filter_set!== undefined)
                    ? FIRST_QUERY.filter_set.filters
                    : JSON.parse(FIRST_QUERY.filters);  // there are two cases. One is from loading the data from DB, the other at runtime.


                // this is the case in which I am loading the query from the DB. In this case I have the filter_set.
                if(FIRST_QUERY.filter_set){
                    queryActions.updateQuery(FIRST_QUERY.filter_set.filters);
                }

                // assign cohort samples to first_query
                if(analysis_type === "cohort" ){
                    // if loading, I need to get the cohort first;
                    if(samples_cohort!== undefined){
                        first_query_to_run.samples_germline = formatSamples(samples_cohort,0);
                    }
                }



                if (validTypes.includes(analysis_type)) {
                    actions.set_current_query(FIRST_QUERY);
                    loadGeneLists(FIRST_QUERY.extra);
                    // set gene operation;
                    let operator_applied = FIRST_QUERY.extra.find(s => s.hasOwnProperty("gene_list_operator"));
                    let geneOperation = (operator_applied!== undefined) ? operator_applied.gene_list_operator : "union";
                    props.geneListActions.setGeneOperation(geneOperation);
                    queryRunner(first_query_to_run, false, analysis_type, first_analysis.localId);
                    setStudyLoaded(true);
                }
                else{
                    actions.set_current_query(FIRST_QUERY);
                    loadGeneLists(FIRST_QUERY.extra);
                    // set gene operation;
                    let operator_applied = FIRST_QUERY.extra.find(s => s.hasOwnProperty("gene_list_operator"));
                    let geneOperation = (operator_applied!== undefined) ? operator_applied.gene_list_operator : "union";
                    props.geneListActions.setGeneOperation(geneOperation);
                    ///
                    props.geneListActions.add_to_all_genes(first_query_to_run.gene);
                    queryRunner(first_query_to_run, false, analysis_type, props.studySettings.current_analysis, props.studySettings.current_query);
                    setStudyLoaded(true);

                }
            }
        }
    };


    /* Load a predefined gene list from an array provided */
    const loadGeneLists = (lists) => {

        lists.forEach(function(s){
            if(s.hasOwnProperty("id") && s.hasOwnProperty("type")){
                props.geneListActions.add_applied_list(s);
            }
        });

    }

    /* Full tag catalog used when a query filters by tagged variants. */
    const loadTaggedVariantsForQuery = () => {
        get_all_variants(config.apis_configuration.api_phenostore, props.token)
            .then(r => r.json())
            .then(json => {
                setTaggedVariantsForQuery(Array.isArray(json?.data) ? json.data : []);
            });
    }

    /* Fetch all tagged variants from the phenostore server */
    const getAllTaggedVariants = () => {

        loadTaggedVariantsForQuery();

        if(geneNames.length!==0){
            tagged_variants_request(config.apis_configuration.api_phenostore, props.token, {genes: geneNames})
            .then(r => r.json())
            .then(function(json){
                console.log(json);
                // process ...
                setFlagged(json);


            });
        }
        else{
            get_all_variants(config.apis_configuration.api_phenostore, props.token)
            .then(r => r.json())
            .then(json => setFlagged(json));
        }

       

    }

    /* Load the full catalog once, so a later query is not limited to genes from the last SNV hits. */
    useEffect(function(){
        if(props.token){
            loadTaggedVariantsForQuery();
        }
    }, [props.token]);

    /* Tags for genes in the last SNV response, used to mark rows already on screen. */
    useEffect(function(){

        if(geneNames.length!==0){
            tagged_variants_request(config.apis_configuration.api_phenostore, props.token, {genes: geneNames})
            .then(r => r.json())
            .then(function(json){
                setFlagged(json);
            });
            setInitialize(true);
        }


    }, [JSON.stringify(geneNames)]); // Trigger on content change

    /* Load a study if a parameter is passed in the URL */
    const getStudyLoad = () => {

        let bool= getIfWithParameters();
        return (bool && props.location.state !== null) ? props.location.state.loadStudy : false;

    }

    /* Check if a parameter is passed in the URL */
    // TODO: move outside the component;

    const getIfWithParameters = () => {
        return (props.location!== undefined && props.location.state!== undefined);
    }

    /* This useEffect detects a change in the sidebar and in the summary of variants. If a change occured, it loads a study and runs a query against a cohort */
    useEffect(function(){

        const open = STATIC_VARIABLES.SIDEBAR_OPTIONS.includes(props.sidebarSettings.page);
        setOpenDrawer(open);
        let bool= getIfWithParameters();
        let samples_cohort = (bool && props.location.state!== null) ? props.location.state.samples_cohort : false;
        let loadStudy = getStudyLoad();
        if(loadStudy && !studyLoaded){
            runQuery(samples_cohort);
            setOpenDrawer(false);
        }
        else if(props.studySettings.analysisType === "instand" && !studyLoaded){

            runQuery();
            setOpenDrawer(false);

        }


    }, [props.sidebarSettings, summary, props.studySettings.analysisType ]);




    const updateParticipantsData = (experimentList, keepTotal) => {

        let { token } = props;
        setLoadingParticipants(true);
        // now collect experiments
        const data_object = getExperimentsViewBody(experimentList);

        experiments_view_with_postembargo(config.apis_configuration.api_data_management, token, data_object)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          // get the IDs and call phenostore;
          let id_array = data["items"].map(s => s["Participant_ID"]);
          let obj = getParticipantBySampleBody(id_array);
      
          getParticipantBySample(config.apis_configuration.api_phenostore, token, obj)
            .then(response => {
              if (!response.ok) {
                setLoadingParticipants(false);
                setParticipants({ participants: [], map_by_exp: [], total: null });
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.json();
            })
            .then(patients => {
              setLoadingParticipants(false);
              const participants_table = processParticipants(patients, data, experimentGeneMap);
      
              if (keepTotal) {
                setParticipants(prevState => ({
                  ...prevState,
                  participants: participants_table,
                  map_by_exp: data["items"],
                  experiments: experimentList,
                }));
              } else {
                setParticipants({
                  participants: participants_table,
                  map_by_exp: data["items"],
                  experiments: experimentList,
                  aggregations: patients["aggregations"],
                  total: id_array.length,
                });
              }
            })
            .catch(error => {
              setLoadingParticipants(false);
              setParticipants({ participants: [], map_by_exp: [], total: null });
              console.error('Error fetching participants from phenostore:', error);
              // Handle the error here, e.g., set error state or display a message to the user
            });
        })
        .catch(error => {
          setLoadingParticipants(false);
          setParticipants({ participants: [], map_by_exp: [], total: null });
          console.error('Error fetching experiments:', error);
          // Handle the error here, e.g., set error state or display a message to the user
        });


    }

    /**
     * Fetch participants for the given experiment IDs (from CNV aggregation) and merge with existing participants
     * so the Participant table shows union of SNV + CNV variant participants (each displayed once).
     */
    const fetchParticipantsFromCnvExperiments = (experimentIds) => {
        if (!Array.isArray(experimentIds) || experimentIds.length === 0) return;
        rememberFullExperimentIds(experimentIds);
        const config = window.config;
        const { token } = props;
        if (!config?.apis_configuration?.api_data_management || !config?.apis_configuration?.api_phenostore || !token) return;
        setLoadingParticipants(true);
        const data_object = getExperimentsViewBody(experimentIds);
        experiments_view_with_postembargo(config.apis_configuration.api_data_management, token, data_object)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP error! Status: ${r.status}`);
                return r.json();
            })
            .then((data) => {
                const id_array = (data?.items || []).map((s) => s.Participant_ID).filter(Boolean);
                if (id_array.length === 0) {
                    setLoadingParticipants(false);
                    return;
                }
                const obj = getParticipantBySampleBody(id_array);
                return getParticipantBySample(config.apis_configuration.api_phenostore, token, obj)
                    .then((r) => {
                        if (!r.ok) {
                            setLoadingParticipants(false);
                            throw new Error(`HTTP error! Status: ${r.status}`);
                        }
                        return r.json();
                    })
                    .then((patients) => {
                        setLoadingParticipants(false);
                        const experimentGeneMap = []; // CNV-only: no SNV sample_agg
                        const participants_table = processParticipants(patients, data, experimentGeneMap);
                        setParticipants((prev) => {
                            const prevParts = prev?.participants || [];
                            const prevMap = prev?.map_by_exp || [];
                            const byReportId = new Map();
                            prevParts.forEach((p) => byReportId.set(p.report_id, p));
                            participants_table.forEach((p) => byReportId.set(p.report_id, p));
                            const mergedParticipants = [...byReportId.values()];
                            const mapKey = (item) => `${item.Participant_ID || ""}:${item.ExperimentID || ""}`;
                            const mapByKey = new Map();
                            prevMap.forEach((item) => mapByKey.set(mapKey(item), item));
                            (data?.items || []).forEach((item) => mapByKey.set(mapKey(item), item));
                            const mergedMapByExp = [...mapByKey.values()];
                            const prevExps = prev?.experiments || [];
                            const allExps = [...new Set([...prevExps, ...experimentIds])];
                            return {
                                participants: mergedParticipants,
                                map_by_exp: mergedMapByExp,
                                experiments: allExps,
                                total: mergedParticipants.length,
                                aggregations: patients?.aggregations ?? prev?.aggregations,
                            };
                        });
                    });
            })
            .catch((err) => {
                setLoadingParticipants(false);
                console.error("Error fetching participants from CNV experiments:", err);
            });
    };

    const clearVariantsOnly = () => {
        setVariants({ snv: empty_json });
        setGeneNames([]);
        setVariantTablesClearedByParticipantSelection(true); // Clear tables without changing gene (Participant table counts stay stable)
        props.selectVariantActions.resetSelectedTableRows();
    };

    /* run a search across query where gene and samples are given */
    // TODO: can be simplified and generalized.
    const runSearchAcrossQuery = (gene, samples, fromParticipantTable, queryMeta = {}) => {
        setVariantTablesClearedByParticipantSelection(false); // Resume normal variant view (gene selection or Show Participant Variants)
        const preservedSampleList = Array.isArray(sampleList) ? [...sampleList] : [];
        // When user selects a gene from the gene table (not "Show Participant Variants"), use cohort experiments for CNV query
        if (!fromParticipantTable) {
            if (gene === "All" && fullExperimentIds.length > 0) {
                setSampleList(fullExperimentIds);
            } else if (gene !== "All") {
                setSampleList([]);
            }
        }

        let config =window.config;

        let { query }  = props;

        delete query["variant_type"];

        let gene_to_search;

        // samples..

        // why this one? review;
        if(Array.isArray(gene) && gene.length === 0 ){
            gene_to_search = true;
            setSelectedGeneForVariantView("All");
        }
        else if(Array.isArray(gene) && gene.length > 0){
            query.gene = gene;
            gene_to_search = gene[0];
            setSelectedGeneForVariantView(gene.length === 1 ? gene[0] : "All");
        }
        else if(gene== "All"){
            query.gene = (Array.isArray(summary) && summary.length > 0) ? summary.map(item => item.gene) : [];
            gene_to_search = gene;
            setSelectedGeneForVariantView("All");
            // test if it makes sense.
            samples = "ALL_SAMPLES"
        }
        else{
            query.gene = [gene];
            gene_to_search = gene;
            setSelectedGeneForVariantView(gene);
        }

        // make call for variants;

        // "Show Participant Variants" with "All" selected: use full gene list so SNV and CNV queries get all genes (same as left table)
        if(Array.isArray(samples) && samples.length>0 && fromParticipantTable  &&
        (!gene_to_search || gene_to_search === true)){
            let tot_genes = [];

            // 1) Prefer cohort query gene list (props.geneList) – same list the user queried with (TTN, ADAL, etc.)
            if (Array.isArray(props.geneList) && props.geneList.length > 0) {
                tot_genes = props.geneList.map((g) => (typeof g === "string" ? g : String(g))).filter(Boolean);
            }
            // 2) Otherwise merge SNV summary genes + CNV aggregation genes (summary is SNV-only when both exist)
            if (tot_genes.length === 0) {
                const summaryGenes = (Array.isArray(summary) ? summary.map((item) => item.gene) : []).filter(Boolean);
                const cnvGenes = (Array.isArray(cnvAggregations) ? cnvAggregations.map((a) => a.Gene_name) : []).filter(Boolean);
                tot_genes = [...new Set([...summaryGenes, ...cnvGenes])];
            }
            // 3) Fallback: genes from experimentGeneMap for selected participant's experiments
            if (tot_genes.length === 0) {
                const sampleAgg = Array.isArray(experimentGeneMap) ? experimentGeneMap : [];
                sampleAgg.filter((s) => samples.includes(s.experiment)).forEach(function(d) {
                    const genes = (d.genes || []).map((s) => s.gene);
                    tot_genes = tot_genes.concat(genes);
                });
                tot_genes = [...new Set(tot_genes)];
            }

            gene_to_search = tot_genes;
            query.gene = tot_genes;
            setSelectedGeneForVariantView(tot_genes.length === 1 ? tot_genes[0] : (tot_genes.length > 0 ? tot_genes : "All"));
        }

        if(gene_to_search!== undefined){

            if (fromParticipantTable) {
                setVariants({ snv: empty_json });
                setGeneNames([]);
                props.selectVariantActions.resetSelectedTableRows();
                // Set sample list immediately so CNV table can fetch (e.g. when only CNV results, SNV response won't set it)
                if (Array.isArray(samples) && samples.length > 0) {
                    setSampleList(samples);
                }
            }

            if (Array.isArray(samples) && samples.length === 0 && fromParticipantTable) {
                setSampleList([]);
                return; 
            }

            let sample_list;
            if(gene.length!== 0 ){
                const found = (Array.isArray(summary) && summary.length > 0) ? summary.find(s => s.gene === gene_to_search) : null;
                sample_list = found?.sample_list ?? [];
            }

            const cohortIds = (Array.isArray(cohort) && cohort.length > 0) ? cohort.map(s => s.sample_id) : [];
            const isSpecificGene = gene_to_search && gene_to_search !== "All" && !Array.isArray(gene_to_search);
            // In cohort mode when selecting a gene: always use full cohort for API calls (variants, experiments_view, CNV), not just experiments that have this gene
            if (!fromParticipantTable && cohortIds.length > 0) {
                sample_list = cohortIds;
            } else if (
                isSpecificGene
                && !fromParticipantTable
                && props.studySettings?.analysisType === "search_across"
            ) {
                const snvExps = Array.isArray(sample_list) ? sample_list : [];
                const experimentFallback = fullExperimentIds.length > 0 ? fullExperimentIds : preservedSampleList;
                sample_list = mergeGeneExperimentIds(snvExps, cnvAggregations, gene_to_search, experimentFallback);
            }

            // Gene has 0 SNVs (e.g. CNV-only): avoid /variants call with samples_germline: [].
            if (isSpecificGene && !fromParticipantTable && (!sample_list || sample_list.length === 0)) {
                if (cohortIds.length > 0) {
                    sample_list = cohortIds;
                    setSampleList(sample_list);
                } else if (props.studySettings?.analysisType === "search_across") {
                    setVariants({ snv: empty_json });
                    setGeneNames([]);
                    props.selectVariantActions.resetSelectedTableRows();
                    setLoading(false);
                    return;
                } else {
                    setVariants({ snv: empty_json });
                    setGeneNames([]);
                    props.selectVariantActions.resetSelectedTableRows();
                    setLoading(false);
                    return;
                }
            }

            const participantSnvTotal = Number(queryMeta?.snvTotal) || 0;
            const shouldLimitParticipantSnv = fromParticipantTable && participantSnvTotal > SNV_DISPLAY_LIMIT;
            const snvRequestSize = shouldLimitParticipantSnv ? SNV_DISPLAY_LIMIT : -1;

            // to get the variants from this API.
            query.size = snvRequestSize;

            //samples check here..

            if(samples === "ALL_SAMPLES"){

                let query = getCurrentQuery(props.studies, props.studySettings);
                queryViewer(query, props.studySettings.analysisType);
            }
            else{
                if(samples!== undefined && samples.length!==0){


                    // get SAMPLES_ALL settings;

                    // gt to fix // GET from Redux;
                    let samples_settings = props.samples;

                    query.samples_germline = samples.map(s => {
                        return createDefaultSample(s, samples_settings);
                    })
                }
                else{
                    if(sample_list!== undefined){
                        let samples_settings = props.samples;
                        query.samples_germline = sample_list.map(s => {
                            return createDefaultSample(s, samples_settings);});
                    }
                    }

                // When selecting a specific gene (e.g. from CNV-only summary), fetch experiments view and participants
                // once using the known experiment list. Do not recalculate this later from the returned SNV hits:
                // those hits may be capped (e.g. first 1000), which can miss experiments and shrink the Participant table.
                const hasKnownParticipantExperimentList = !fromParticipantTable && Array.isArray(sample_list) && sample_list.length > 0;
                if (hasKnownParticipantExperimentList) {
                    setSampleList(sample_list);
                    updateParticipantsData(sample_list);
                }

                let api_endpoint  = config.api_endpoint;
                let { token } = props;

                setLoading(true);
                // run query;

                // force variant hits, capped for large participant-specific SNV queries
                query["size"] = snvRequestSize;

                // TODO: move outside the methods, create a general fetch function for collecting the variants;
                getSAVariants(api_endpoint, token, omitDirGermlineGtForApi(query))
                    .then(response => {

                        let variantResp = check_variant_response(response);
                        return variantResp;
                        }
                    )
                    .then((json) => {
                        if(typeof json === "object"){
                            setVariants(json);
                            
                            let geneNames = extractGeneNames(json);
                            setGeneNames(geneNames);

                            // reset all selected variants;


                            props.selectVariantActions.resetSelectedTableRows();

                            let es_hits = ( typeof json.snv.hits.total === 'object' ) ? json.snv.hits.total.value : json.snv.hits.total;

                            if (shouldLimitParticipantSnv && es_hits > SNV_DISPLAY_LIMIT) {
                                alert(`More than ${SNV_DISPLAY_LIMIT} SNV variants have been found for the selected participant(s). Only the first ${SNV_DISPLAY_LIMIT} results were loaded. Please add more filters to your query to see all results.`);
                            }

                            // update participants...
                            if(es_hits!==0){
                                let samples_all = [];
                                json.snv.hits.hits.forEach(s => {
                                    if(s.fields.samples_germline!== undefined && Array.isArray(s.fields.samples_germline)){
                                        samples_all = samples_all.concat(s.fields.samples_germline.map(g => g.sample ));
                                    }
                                });
                                let experimentList = [...new Set(samples_all)];
                                // In cohort mode keep full cohort so CNV table and other calls use all experiments (E334733, E408451, E543857), not just those with hits.
                                // If we already had a known list for this gene, keep it: experimentList is derived from loaded SNV hits and may be incomplete when the response is capped.
                                const cohortIdsForList = (Array.isArray(cohort) && cohort.length > 0) ? cohort.map(s => s.sample_id) : [];
                                if (!fromParticipantTable) {
                                    const participantExperimentList = cohortIdsForList.length > 0
                                        ? cohortIdsForList
                                        : (hasKnownParticipantExperimentList ? sample_list : experimentList);
                                    setSampleList(participantExperimentList);
                                    if (!hasKnownParticipantExperimentList) {
                                        // create a map with Experiment ID, Participant ID and N_variants (use full cohort so participant table shows all)
                                        updateParticipantsData(participantExperimentList);
                                    }
                                } else if (Array.isArray(samples) && samples.length > 0) {
                                    setSampleList(samples);
                                } else {
                                    setSampleList(experimentList);
                                }

                            }


                            setLoading(false);
                            setSnackOpen(true);
                            props.actions.set_sa_query(query);

                            // Error message
                            if(json.message!== undefined){
                                if(typeof json.message === "string" ){
                                    setSnackText(json.message);
                                    setSnackColor("error");
                                }
                            }  // All Good
                            else {
                                setSnackText("SNV Query Run Successfully!");
                            }
                        }
                        else{

                            setVariants({snv:empty_json});
                    
                            setGeneNames([]);

                            props.selectVariantActions.resetSelectedTableRows();
                            setLoading(false);
                            setSnackOpen(true);

                            if(json.message!== undefined){
                                if(typeof json.message === "string" ) {
                                    setSnackText(json.message);
                                    setSnackColor("error");
                                }
                            }
                            else {
                                setSnackText("Query Not Successful");
                                setSnackColor("error");
                            }
                        }
                    })
                    .catch(function() {
                        // catch
                        setSnackOpen(true);
                        setSnackText("Request Failed");
                        setSnackColor("error");
                        setLoading(false);

                    });
            }
        }
    };

    /* Save a query. Collects a query, the json of the query pauload, analysis type id, type of variant (tagged all, tagged selected) */
    // TODO: variant type is misleading as in phenostore, variant type refers to SNV, Deletion, Inversion, etc...

    const saveQuery = (queryNew, json, analysis_type, variant_type) => {

        let { studyActions, currentAnalysis, study_id, studySettings, studyBucket  } = props;
        // check the inheritances and create the analysis;
        let date = date_generator();

        let my_analysis = getCurrentAnalysis(studySettings, studyBucket)

        const count = my_analysis.analysis.queries.length + 1;

        const localID = currentAnalysis + "_" + count;
        
        const es_hits = ( typeof json.snv.hits.total === 'object' ) ? json.snv.hits.total.value : json.snv.hits.total;

        const total = (json.snv) ? es_hits : 0;

        let extra = [...props.applied_lists];

        // update tagged variant info search;
        let v_type= (variant_type!== undefined) ? {variant_type: variant_type} : {variant_type: "GPAP"};
        let v_type_obj= extra.find(s => s["variant_type"]!==undefined);
        if(v_type_obj !== undefined){
            v_type_obj["variant_type"] = v_type["variant_type"]
        }
        else{
            extra.push(v_type);
        }


        // current filter set;

        let currentFilterSet_id = Object.keys(props.currentFilterSet).length!== 0
            ? props.currentFilterSet.id
            : -1;

        let currentFilterSet_name = Object.keys(props.currentFilterSet).length!== 0
            ? props.currentFilterSet.filter_set_name
            : "";

        // add gene operator used for genes;
        if(queryNew.gene.length>0){
            extra.push({gene_list_operator: props.geneList_operator});
        }

        // create query
        const data = {
            study_id: study_id ,
            analysis_id: currentAnalysis,
            query: {
                "localId": localID,
                "name": "query_" + count,
                "query_name": `Query ${count}`,
                "analysis_id": currentAnalysis,
                "filter_set_id": currentFilterSet_id,
                "filter_set_name": currentFilterSet_name,
                "filters": JSON.stringify(queryNew),
                "creationDate": date,
                "outputs": [{"n_variants": total, "run_date": date}],
                "extra": extra   // applied lists (e.g. predefined filters or positions etc..)
            }
        };

        studyActions.create_query(data);

        let { study_saved } = props.studySettings;

        let study_status = (study_saved) ? false : study_saved;

        studyActions.setStudySettings({
            current_query: localID,
            study_saved: study_status,
            analysisType: analysis_type});

    };


    /* Main function to run queries when changes are detected on the tabs of the dashboard.  */
    // TODO: Can be simplified and made more generic;
    const queryRunner = async (query_to_run, store, analysis_type, analysis_id, query_id) => {

        let {token, studySettings, studyBucket} = props;
        let config = window.config;
        let api_endpoint = config.api_endpoint;
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        setLoading(true);
        setSelectedGeneForVariantView("All");
        setVariantTablesClearedByParticipantSelection(false);
        setLoadingParticipants(true);
        setParticipants({ participants: [], map_by_exp: [], total: null, experiments: [] });
        setSampleList([]);
        setCnvAggregations(null);
        props.selectVariantActions.resetSelectedTableRows();

        //check index value if Affected return true if Unaffected return false

        if(query_to_run.hasOwnProperty("samples_germline")){
            query_to_run.samples_germline.map(function(sample){if (sample.index==="Affected") {sample.index=true;} else {if  (sample.index==="Unaffected") {sample.index=false;} } })
        }


        // check endpoint to query....
        // if family or search across...

        if (analysis_type === undefined) {
            analysis_type = studySettings.analysisType;
        }

        if (analysis_type === "search_across" || analysis_type === "cohort") {


            let variant_type = query_to_run["variant_type"];

            delete query_to_run["variant_type"];


            // genes
            let geneList = props.geneList;
            let query = {...query_to_run};
            if (query.gene.length === 0) {
                query.gene = geneList;
            }

           //

            if (analysis_type !== "cohort") {
                // search across use case;

                // if this comes by clicking the query tab;
                if(analysis_id === undefined){
                    let samples_to_analyze = props.samples;
                    query.samples_germline = formatSamples(samples_to_analyze, 0);
                }
                else{
                     query.samples_germline = getSamplesFromBucket(studySettings,studyBucket, analysis_id,query_to_run.samples_germline);
                }


            }
            else if(analysis_type === "cohort"){
                if(query_to_run.samples_germline.length !== 0 && query_to_run.samples_germline.find(s => s.sample_id === "ALL_SAMPLES") === undefined){

                    //be sure query to run has settings;
                    let samples_to_analyze = props.samples;

                    // if this comes by clicking the query tab;
                    if(query_id!== undefined){
                        let study = getStudy(studySettings,studyBucket);
                        let current_analysis = study.study.analyses.find(s => s.localId === analysis_id);
                        samples_to_analyze = current_analysis.samples;
                    }

                    let my_cohort= assignSettings(query_to_run.samples_germline, samples_to_analyze);
                    query.samples_germline = my_cohort
                    setCohort([...my_cohort]);

                }
                else{
                    query.samples_germline = cohort
                }

            }

            // Load variants (not just aggregations) so the SNV table shows results for "All" genes / initial view,
            // but cap the payload at 1000 hits. The real total still comes back in hits.total.value, so we can warn
            // the user when there are more than 1000 SNVs (see SNV_DISPLAY_LIMIT handling in the response below).
            query.size = SNV_DISPLAY_LIMIT;

            // samples_somatic

            query.samples_somatic = [];


            getSAVariants(api_endpoint, token, omitDirGermlineGtForApi(query))
                .then(response => {
                    let variantResp = check_variant_response(response);
                    return variantResp;
                    }
                )
                .then((json) => {

                    setLoading(false);

                    // get the summary

                    let {snv_total} = getTotalFromJson(json);

                    // Only the first SNV_DISPLAY_LIMIT variants are loaded for the "All"/initial view.
                    // Warn the user (like the CNV >10k warning) when there are more, so they can narrow the query.
                    if (snv_total > SNV_DISPLAY_LIMIT) {
                        alert(`More than ${SNV_DISPLAY_LIMIT} SNV variants have been found. Only the first ${SNV_DISPLAY_LIMIT} are shown. Please add more filters to your query to see all results.`);
                    }


                    if (snv_total !== 0) {
                        const output = map_sa_output(json);

                        const {summary, sample_agg} = output;
                        setSummary(summary);
                        setSampleAgg(sample_agg);

                        // Use the known query experiment IDs for the Participant table.
                        // Do not derive them from the returned SNV aggregation/hits here: for "All" we cap SNV hits
                        // to SNV_DISPLAY_LIMIT, so deriving experiments from the response can miss cohort samples.
                        let distinct = Array.isArray(query.samples_germline)
                            ? query.samples_germline.map(s => s.sample_id).filter((id) => id && id !== "ALL_SAMPLES")
                            : [];
                        if (distinct.length === 0) {
                            let all_samples = [];
                            summary.forEach(function (item) {
                                all_samples = all_samples.concat(item.sample_list)
                            });
                            distinct = [...new Set(all_samples)];
                        }
                        setSampleList(distinct);
                        rememberFullExperimentIds(distinct);

                        setLoadingParticipants(true);
                        const data_object = getExperimentsViewBody(distinct);

                        experiments_view_with_postembargo(config.apis_configuration.api_data_management, token, data_object)
                            .then(r => r.json())
                            .then(data => {

                                // get the IDs and call phenostore;
                                let id_array = data["items"].map(s => s["Participant_ID"]);
                                let obj = getParticipantBySampleBody(id_array);

                                getParticipantBySample(config.apis_configuration.api_phenostore, token, obj)
                                    .then(r => {
                                        if (!r.ok) {
                                            setLoadingParticipants(false);
                                            setParticipants({
                                                participants: [],
                                                map_by_exp:[],
                                                total: null})
                                            throw new Error(`HTTP error! Status: ${r.status}`);

                                        }
                                        return r.json();
                                    })
                                    .then(participants => {

                                        setLoadingParticipants(false);
                                        const participants_table = processParticipants(
                                            participants,          
                                            data,              
                                            experimentGeneMap
                                          );

                                        // json objects of participants;
                                        setParticipants({
                                            participants: participants_table,
                                            map_by_exp: data["items"],
                                            experiments: distinct,
                                            total: id_array.length,
                                            aggregations: participants["aggregations"]
                                        })
                                    })
                                    .catch(error => {
                                        setLoadingParticipants(false);
                                        setParticipants({
                                            participants: [],
                                            map_by_exp:[],
                                            total: null})

                                        console.error('Error fetching participants:', error);
                                        // Handle the error here, e.g., set error state or display a message to the user
                                    });

                            });

                        if (store) {
                            try{
                                saveQuery(query, json, analysis_type, variant_type);
                            }
                            catch(error){
                                setSnackOpen(true);
                                setSnackText(STATIC_VARIABLES.QUERY_CREATION_ERROR);
                                setSnackColor("error");
                                setLoading(false);
                            }

                        }

                        if (store || analysis_type !== "family_trio" || store || analysis_type !== "somatic" || analysis_type!== "instand" ) {
                            // TO REVIEW

                            props.studyActions.update_query_output({
                                query_id: (query_id !== undefined) ? query_id : studySettings.current_query,
                                analysis_id: analysis_id,
                                n_variants: snv_total,
                                study_id: studySettings.id
                            })
                        }

                    }
                    else {
                        // snv_total === 0: still create the tab and set up experiments so CNV aggregation is triggered
                        // Use [] (truthy) instead of false so Variant_Table renders the ParticipantTable (guarded by `if (summary)`)
                        setSummary([]);
                        setSampleAgg([]);

                        let zeroHitExpIds = [];
                        if (Array.isArray(cohort) && cohort.length > 0) {
                            zeroHitExpIds = cohort.map(s => s.sample_id);
                        } else if (Array.isArray(query.samples_germline)) {
                            zeroHitExpIds = query.samples_germline.map(s => s.sample_id).filter(Boolean);
                        }

                        if (zeroHitExpIds.length > 0) {
                            setSampleList(zeroHitExpIds);
                            rememberFullExperimentIds(zeroHitExpIds);

                            setLoadingParticipants(true);
                            const data_object = getExperimentsViewBody(zeroHitExpIds);

                            experiments_view_with_postembargo(config.apis_configuration.api_data_management, token, data_object)
                                .then(r => r.json())
                                .then(data => {
                                    let id_array = data["items"].map(s => s["Participant_ID"]);
                                    let obj = getParticipantBySampleBody(id_array);

                                    getParticipantBySample(config.apis_configuration.api_phenostore, token, obj)
                                        .then(r => {
                                            if (!r.ok) {
                                                setLoadingParticipants(false);
                                                setParticipants({ participants: [], map_by_exp: [], total: null });
                                                throw new Error(`HTTP error! Status: ${r.status}`);
                                            }
                                            return r.json();
                                        })
                                        .then(participants => {
                                            setLoadingParticipants(false);
                                            const participants_table = processParticipants(participants, data, []);
                                            setParticipants({
                                                participants: participants_table,
                                                map_by_exp: data["items"],
                                                experiments: zeroHitExpIds,
                                                total: id_array.length,
                                                aggregations: participants["aggregations"]
                                            });
                                        })
                                        .catch(error => {
                                            setLoadingParticipants(false);
                                            setParticipants({ participants: [], map_by_exp: [], total: null });
                                            console.error('Error fetching participants:', error);
                                        });
                                });
                        }

                        if (store) {
                            try {
                                saveQuery(query, json, analysis_type, variant_type);
                            } catch (error) {
                                setSnackOpen(true);
                                setSnackText(STATIC_VARIABLES.QUERY_CREATION_ERROR);
                                setSnackColor("error");
                                setLoading(false);
                            }
                        }

                        if (store || analysis_type !== "family_trio" || store || analysis_type !== "somatic" || analysis_type !== "instand") {
                            props.studyActions.update_query_output({
                                query_id: (query_id !== undefined) ? query_id : studySettings.current_query,
                                analysis_id: analysis_id,
                                n_variants: 0,
                                study_id: studySettings.id
                            });
                        }
                    }

                    // launch query to data management to get phenostore IDs...

                    setVariants(json);
                    setGeneNames(extractGeneNames(json));

                    props.selectVariantActions.resetSelectedTableRows();
                    setSnackOpen(true);
                    if (json.message !== undefined) {
                        setSnackText(json.message);
                        setSnackColor("error");
                    } else {
                        setSnackText(STATIC_VARIABLES.SUCCESS_RUN);
                        setSnackColor("success");
                    }
                })
                .catch(function () {                        // catch
                    setSnackOpen(true);
                    setSnackText(STATIC_VARIABLES.UNAVAILABLE_SERVER);
                    setSnackColor("error");
                    setLoading(false);
                });
        } else {
            // remove not allowed keys;

            let variant_type = query_to_run["variant_type"];

            delete query_to_run["variant_type"];

            // Take somatic samples from somatic_query_default
            if (props.instandGeneticFindingsTab === "somatic"){
                query_to_run.samples_germline = []
                //query_to_run.samples_somatic = JSON.parse(JSON.stringify(somatic_query_default.samples_somatic));
                //query_to_run.samples_somatic = props.somatic_samples[0].gt_settings;
                //query_to_run.samples_somatic = formatSamples(props.somatic_samples ,0);

                let study = getStudy(props.studySettings, props.studyBucket);
                let analyses = study.study.analyses.find(a => a.localId === analysis_id);

                // somatic query samples..
                query_to_run = configureSomaticQuery(query_to_run, analyses, somatic_query_default, props, selectedDir);

              /*   //Case when Switching between analysis
                if (analyses && analyses.samples_somatic && analyses.samples_somatic.length > 0){
                    query_to_run.samples_somatic = analyses.samples_somatic[0].gt_settings                    ;
                }
                
                //Case of going to the First analysis already preconfigured
                else if (analyses && !analyses.samples_somatic){
                    query_to_run.samples_somatic = JSON.parse(JSON.stringify(somatic_query_default.ref_samples_somatic));
                }
                
                //Case of New query
                else{
                    query_to_run.samples_somatic = props.somatic_samples[0].gt_settings;
                }
                 
                //Get tumor & control experiment ids from corresponding DIR
                let tumor_experiment_id = selectedDir.tumor_experiment_id ? selectedDir.tumor_experiment_id : ""
                let control_experiment_id = selectedDir.control_experiment_id ? selectedDir.control_experiment_id : ""

                query_to_run.samples_somatic[0].sample_id_tumor = tumor_experiment_id
                query_to_run.samples_somatic[0].sample_id_control = control_experiment_id

                //Case of Somatic Tumor only (without control)
                if (selectedDir.analysis === "somatic_tumor_only"){
                    delete query_to_run.samples_somatic[0].sample_id_control
                    delete query_to_run.samples_somatic[0].dp_control
                    delete query_to_run.samples_somatic[0].ad_control
                    delete query_to_run.samples_somatic[0].pass_control
                } */

            }

            getVariants(api_endpoint, token, omitDirGermlineGtForApi(query_to_run))
                .then(response => {
                    if (response.status !== 200) {
                        return response.text();
                    }
                    return response.json();       
                    //return check_variant_response(response);
                })
               .then((json) => {
                let error = "";
                let message = json;
                    if (typeof json === 'string'){
                        //error=json;
                        setLoading(false);
                        if(message.includes("html")){
                            message = "Server Error";
                        }


                    }
                    else {
                    message = json['message'];
                    let {snv_total} = getTotalFromJson(json);


                    
                    if(json.status === 404 || json.status_code === 500){
                        error = json.error;
                        json= {snv: empty_json};
                        message = "Server Error";
                    }

                  

                    setVariants(json);
                    let geneNames = extractGeneNames(json);
                    setGeneNames(geneNames);
                    
                    props.selectVariantActions.resetSelectedTableRows();
                    setLoading(false);
                    if (json) {
                        let queryNew = JSON.parse(JSON.stringify(query_to_run));
                        if (store) {
                            saveQuery(queryNew, json, analysis_type, variant_type);
                        }


                        // TO REVIEW
                        props.studyActions.update_query_output({
                            query_id: (query_id !== undefined) ? query_id : studySettings.current_query,
                            analysis_id: analysis_id,
                            n_variants: snv_total,
                            study_id: studySettings.id
                        });
                    }
                }
                        setSnackOpen(true);
                        if (message !== undefined) {
                            if(error!== ""){
                                setSnackText(message + ", server message is: " + error.reason);
                                setSnackColor("error");
                            }
                            else{
                                setSnackText(message);
                                setSnackColor("error");
                            }

                        } else {
                            setSnackText(STATIC_VARIABLES.SUCCESS_RUN);
                            setSnackColor("success");
                        }
                    
                
                })
                .catch(error => {
                    // catch
                    


                    setSnackOpen(true);
                    setSnackText(STATIC_VARIABLES.UNAVAILABLE_SERVER);
                    setLoading(false);
                    setSnackColor("error");
                })

        }

        props.sidebarActions.setView({page: "home"});
    };


    /* This function allows to view a specific variant that was already performed. It is triggered with a click on a tab in the dashboard */
    const queryViewer = (query, analysisType) => {
        // prepare query from STORE;

        let { actions, studyActions } = props;

        // run query;

        studyActions.setStudySettings({current_analysis: query.analysis_id, current_query: query.localId, analysisType: analysisType});
        let query_to_view = (query.filters!== undefined) ? JSON.parse(query.filters) : query.filter_set.filters;

        queryRunner(query_to_view, false, analysisType);

        // run query;
        actions.set_current_query(query);

        setSnackOpen(true);
        setSnackText(STATIC_VARIABLES.SUCCESS_QUERY);
        setSnackColor("success");

    };

    /* Function to delete a specific query */
    const queryDelete = (query) => {
        // prepare query from STORE;

        let {  studyActions, study_id } = props;

        studyActions.delete_query(query, study_id);
        studyActions.setStudySettings({current_query: query.localId});

        if(query.query_id!== undefined){
            delete_query_db(config.apis_configuration.api_nextgpap_server, props.token, query)
                .then(r => r.json()).then(json => {
                if(json["message"] === "Success")
                {
                    alert(STATIC_VARIABLES.SUCCESS_DELETE);
                }
            })
        }
    };

    /* General function to fetch Variants starting from a variant ith */
    const fetchVariantData = (fromVariant) => {

        let config =window.config;
        let { token  } = props;
        let api_endpoint  = config.api_endpoint;

        let query = props.query;

        const queryForApi = omitDirGermlineGtForApi({ ...query });
        queryForApi.from = fromVariant;

        setLoading(true);

        getVariants(api_endpoint, token, queryForApi)
            .then(response => response.json())
            .then((json) => {
                   setLoading(false);
                   setVariants(json);
                   setGeneNames(extractGeneNames(json));
                
                    props.selectVariantActions.resetSelectedTableRows();
                }
            )
    };

    /* Reset the filters and the table */
    // TODO: check if neeeded;
    const resetAll = () => {

        let { actions, geneListActions, positionActions, resetDirGermlineGtForm } = props;
        // Clear BED/coordinate uploads before resetting the query object, otherwise QueryGenerator
        // can run after updateQuery and merge stale position_uploads back into query.chrom.
        positionActions.reset_ranges();
        actions.updateQuery(query_sample);
        resetDirGermlineGtForm();
        geneListActions.resetGenes();
        setVariants({...DEFAULT_GPAP_RESPONSE});
        setGeneNames([]);

    }

    /* Reset all variant filters, and gene filters applied.  */
    const resetQuery = () => {

        let { actions, geneListActions, positionActions, resetDirGermlineGtForm } = props;
        positionActions.reset_ranges();
        actions.updateQuery(query_sample);
        resetDirGermlineGtForm();
        geneListActions.resetGenes();
        // re-render
        setSnackText("All filters were reset")
        setSnackOpen(true);
        setSnackColor("success");
    };

    /* Switch Analysis Panels in the dashboard */
    const switchAnalysis = (analysis_id, query_id) => {

        let study = getStudy(props.studySettings, props.studyBucket);

        let analyses = study.study.analyses.find(a => a.localId === analysis_id);

        if(analyses!== undefined){
            const FIRST_QUERY = analyses.queries[0];

            let query = (FIRST_QUERY.filter_set!== undefined) ? FIRST_QUERY.filter_set.filters : JSON.parse(FIRST_QUERY.filters);
            let id = FIRST_QUERY.localId;

            props.queryActions.updateQuery(query);
            queryRunner( query, false, analyses.analysis_type, analysis_id, query_id);
            props.studyActions.setStudySettings({current_query: id});
        }
    };

    /* Switch Query in the dashboard */

    const switchQuery = (query_id) => {

        let analysis = getCurrentAnalysis(props.studySettings, props.studyBucket);

        if(analysis!== undefined){
            let query_found = analysis.analysis.queries.find(a => a.localId === query_id);
            if(query_found!== undefined){
                let query =(query_found.filter_set!== undefined) ? query_found.filter_set.filters : JSON.parse(query_found.filters);
                let id = query_found.localId;
                props.queryActions.updateQuery(query);
                queryRunner( query, false, analysis.analysis_type, props.studySettings.current_analysis, query_id);
                // set up applied genes and lists...
                props.geneListActions.init_applied_lists(query_found.extra.filter(s => s.hasOwnProperty("id")));

                // set gene operation;
                let operator_applied = query_found.extra.find(s => s.hasOwnProperty("gene_list_operator"));
                let geneOperation = (operator_applied!== undefined) ? operator_applied.gene_list_operator : "union";
                props.geneListActions.setGeneOperation(geneOperation)

                props.studyActions.setStudySettings({current_query: id});

            }
        }
    };

    /* Standard call to collect variants */

    const standardRun = () => {
        setOpenDrawer(false);
        let query = props.query;
        // run query;
        queryRunner( query, true);
    };


    const handleSnackClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackOpen(false)
    };

    /* =================== RENDERING METHODS ============== */


    const renderResults_View =() => {


        return <React.Fragment>

            { (props.studySettings.analysisType === "instand")
               ? <ManualFiltering
                    size = {'extended'}
                    variants = {variants}
                    queryRunner={standardRun}
                    run_query = {queryRunner}
                    all_tagged_variants = {flagged.data}
                    runSearchAcrossQuery = {runSearchAcrossQuery}
                    loading={loading}
                    loadingParticipants={loadingParticipants}
                    updateParticipantsData={updateParticipantsData}
                    type = {(summary) ? "search_across" : "family"}
                    summary = {summary}
                    sample_agg = {experimentGeneMap}
                    sampleList = {sampleList}
                    participants = {participants}
                    fetchData={fetchVariantData}
                    switchAnalysis = {switchAnalysis}
                    switchQuery={switchQuery}
                    getAllTaggedVariants = {getAllTaggedVariants}
                    resetAll = {resetAll}
                    requestStatus = { snackText }
                    instandGeneticFindingsTab = {props.instandGeneticFindingsTab}
                    />
                : <Results size = {'extended'}
                          variants = {variants}
                          queryRunner={standardRun}
                          run_query = {queryRunner}
                          all_tagged_variants = {flagged.data}
                          runSearchAcrossQuery = {runSearchAcrossQuery}
                          selectedGeneForVariantView={selectedGeneForVariantView}
                          clearVariantsOnly={clearVariantsOnly}
                          variantTablesClearedByParticipantSelection={variantTablesClearedByParticipantSelection}
                          loading={loading}
                          loadingParticipants={loadingParticipants}
                          updateParticipantsData={updateParticipantsData}
                          type = {(summary) ? "search_across" : "family"}
                          summary = {summary}
                          sample_agg = {experimentGeneMap}
                          sampleList = {sampleList}
                          participants = {participants}
                          cohortExperimentIds = {cohortExperimentIds}
                          fullExperimentIds = {fullExperimentIds}
                          onCnvSummaryReady = {setSummary}
                          onCnvAggregationData={setCnvAggregations}
                          onCnvExperimentsForParticipants={fetchParticipantsFromCnvExperiments}
                          cnvAggregations={cnvAggregations}
                          fetchData={fetchVariantData}
                          switchAnalysis = {switchAnalysis}
                          switchQuery={switchQuery}
                          getAllTaggedVariants = {getAllTaggedVariants}
                          resetAll = {resetAll}
                          requestStatus = { snackText }
                />

            }

        </React.Fragment>
    };


    const renderMainView = () => {

        return <Box p={2}>
            <Grid container
                  direction={"row"}
                  justifyContent="center"
                  alignItems="center">

            <Grid item xs={12}>
                { renderResults_View() }
            </Grid>
        </Grid>
        </Box>
    };


    const renderDrawer = () => {
        return  <GPAP_Drawer
            resetQuery = {resetQuery}
            queryRunner = { standardRun }
            variants = {variants}
            queryViewer = { queryViewer }
            queryDelete = {queryDelete}
            cohorts={[]}
            filters = {filters}
            open={openDrawer}/>
    }


    let vertical ="top";
    let horizontal = "center";

    return  <React.Fragment>
        {renderDrawer()}
        <QueryGenerator  all_tagged_variants = {taggedVariantsForQuery} loadingStudy={loading} studyLoaded={studyLoaded}/>
       <div className="mainPanel">

           <Snackbar
               anchorOrigin={{ vertical, horizontal }}
               open={snackOpen}
               autoHideDuration={6000}
               onClose={handleSnackClose}
               TransitionComponent={Grow} // Disable transition
           >
               <Alert onClose={handleSnackClose} severity={snackColor}>
                   <MappedText text={snackText} />
               </Alert>
           </Snackbar>
             {(studyLoaded)
            ? renderMainView()
            : NoView()}
        </div>
    </React.Fragment>

}


//state here is store;
// map the store to the props of MainPanel component;
function mapStateToProps (state) {
    if(state.authorization!==undefined)
    {
        if(state.query !== undefined)
        {
            return { query: {... state.query},
                token: state.authorization.token,
                parsedToken: state.authorization.parsedToken,
                studies: state.studyBucket.studies,
                currentAnalysis: state.studySettings.current_analysis,
                currentFilterSet: state.currentFilterSet,
                study_id: state.studySettings.id,
                studySettings: state.studySettings,
                studyBucket: state.studyBucket,
                sidebarSettings: state.sidebarSettings,
                geneList: state.geneList.geneList,
                geneList_operator: state.geneList.operator,
                applied_lists: state.geneList.applied_lists,
                samples: state.sample_list.samples,
                somatic_samples: state.sample_list.somatic_samples,
                selected_cohort: state.selected_cohort
            }
        }

    }
}

// to send data from MainPanel component to the Store;
const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch),
    studyActions: bindActionCreators(StudyActions, dispatch),
    queryActions: bindActionCreators(QueryActions, dispatch),
    geneListActions: bindActionCreators(GeneActions, dispatch),
    selectVariantActions: bindActionCreators(SelectVariantActions, dispatch),
    sa_queryActions: bindActionCreators(SelectVariantActions, dispatch),
    sidebarActions: bindActionCreators(SidebarActions, dispatch),
    positionActions: bindActionCreators(PositionActions, dispatch),
    resetDirGermlineGtForm: () => dispatch(initialize("dir_germline_gt", { ...DIR_GERMLINE_GT_FORM_RESET }))
});





export default connect(mapStateToProps,mapDispatchToProps)(Genomics);